import React, { useState, useEffect } from 'react';
import { fetchUnseenScenario } from '../services/geminiService';
import { GeneratedScenarioResponse } from '../types';
import { RefreshCw, CheckCircle, XCircle, AlertTriangle, Play, Award } from 'lucide-react';
import { useUser } from '../context/UserContext';

const ScenarioDojo: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<GeneratedScenarioResponse | null>(null);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { addXp, incrementStat, user, markScenarioSeen } = useUser();

  const fetchScenario = async () => {
    setLoading(true);
    setError(null);
    setData(null);
    setSelectedOptionIndex(null);
    try {
      // Pass the list of IDs the user has already seen
      const response = await fetchUnseenScenario(user.completedScenarioIds);
      if (response && response.scenario) {
        setData(response as GeneratedScenarioResponse);
      } else {
        throw new Error("Invalid format");
      }
    } catch (err) {
      setError("Failed to consult the Nunchi Master. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScenario();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelection = (index: number) => {
    if (selectedOptionIndex !== null || !data) return;
    
    setSelectedOptionIndex(index);
    incrementStat('scenariosCompleted');
    
    // Mark this scenario ID as seen so it doesn't appear again
    if (data.id) {
        markScenarioSeen(data.id);
    }

    const selectedOption = data.scenario.options[index];
    
    if (selectedOption.nunchiLevel === 'High') {
      addXp(100, `Perfect Nunchi: ${data.scenario.title}`);
    } else if (selectedOption.nunchiLevel === 'Low') {
      addXp(20, `Poor Nunchi: ${data.scenario.title}`);
    } else {
      addXp(10, `No Nunchi: ${data.scenario.title}`);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-6">
        <div className="w-16 h-16 border-4 border-nunchi-200 border-t-nunchi-700 rounded-full animate-spin mb-6"></div>
        <h2 className="text-xl font-serif font-bold text-nunchi-800 animate-pulse">Consulting the Oracle...</h2>
        <p className="text-nunchi-500 mt-2">Observing social patterns and constructing a scenario.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] text-center px-6">
        <AlertTriangle size={48} className="text-red-400 mb-4" />
        <h2 className="text-xl font-bold text-nunchi-800 mb-2">Connection Lost</h2>
        <p className="text-nunchi-600 mb-6">{error}</p>
        <button 
          onClick={fetchScenario}
          className="px-6 py-2 bg-nunchi-700 text-white rounded-lg hover:bg-nunchi-800 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!data) return null;

  const { scenario } = data;

  return (
    <div className="max-w-2xl mx-auto px-6 pb-20">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xs font-bold tracking-widest text-nunchi-500 uppercase">Scenario Dojo</h2>
        <button 
          onClick={fetchScenario} 
          className="text-nunchi-500 hover:text-nunchi-800 transition-colors flex items-center gap-1 text-sm"
        >
          <RefreshCw size={14} /> Skip Scenario
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-nunchi-200 p-6 md:p-8 mb-8 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-nunchi-300 to-nunchi-600"></div>
        <span className="inline-block px-3 py-1 bg-nunchi-50 text-nunchi-600 text-xs font-bold rounded-full mb-4">
          {scenario.setting}
        </span>
        <h1 className="text-2xl font-serif font-bold text-nunchi-900 mb-4">
          {scenario.title}
        </h1>
        <p className="text-lg text-nunchi-700 leading-relaxed">
          {scenario.description}
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-nunchi-500 uppercase tracking-wider mb-2">
          How do you react?
        </h3>
        
        {scenario.options.map((option, idx) => {
          const isSelected = selectedOptionIndex === idx;
          const isRevealed = selectedOptionIndex !== null;
          
          let cardStyle = "bg-white border-nunchi-200 hover:border-nunchi-400 hover:shadow-md cursor-pointer";
          let icon = <div className="w-6 h-6 rounded-full border-2 border-nunchi-300"></div>;

          if (isRevealed) {
            if (isSelected) {
              if (option.nunchiLevel === 'High') {
                cardStyle = "bg-green-50 border-green-300 ring-1 ring-green-300";
                icon = <CheckCircle className="text-green-600" size={24} />;
              } else if (option.nunchiLevel === 'Low' || option.nunchiLevel === 'None') {
                cardStyle = "bg-red-50 border-red-300 ring-1 ring-red-300";
                icon = <XCircle className="text-red-500" size={24} />;
              }
            } else {
              // Dim other options
              cardStyle = "bg-gray-50 border-gray-100 opacity-60";
            }
          }

          return (
            <div 
              key={idx}
              onClick={() => handleSelection(idx)}
              className={`relative border rounded-xl p-5 transition-all duration-300 ${cardStyle}`}
            >
              <div className="flex items-start gap-4">
                <div className="mt-0.5 flex-shrink-0">
                  {icon}
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <p className={`text-base font-medium ${isRevealed && !isSelected ? 'text-gray-500' : 'text-nunchi-900'}`}>
                      {option.text}
                    </p>
                    {isRevealed && isSelected && option.nunchiLevel === 'High' && (
                       <span className="flex items-center gap-1 text-xs font-bold text-amber-600 bg-amber-100 px-2 py-1 rounded-full whitespace-nowrap">
                         <Award size={12} /> +100 XP
                       </span>
                    )}
                  </div>
                  
                  {isRevealed && isSelected && (
                    <div className="mt-4 pt-4 border-t border-current border-opacity-20 animate-fadeIn">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${
                          option.nunchiLevel === 'High' ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'
                        }`}>
                          Nunchi Level: {option.nunchiLevel}
                        </span>
                      </div>
                      <p className="text-sm opacity-90 leading-relaxed">
                        {option.analysis}
                      </p>
                      
                      {option.nunchiLevel !== 'High' && (
                         <button 
                           onClick={(e) => {
                             e.stopPropagation();
                             fetchScenario();
                           }}
                           className="mt-4 text-xs font-bold underline flex items-center gap-1"
                         >
                           Try Another Scenario <Play size={10} fill="currentColor" />
                         </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {selectedOptionIndex !== null && (
        <div className="mt-8 flex justify-center animate-bounce">
          <button 
            onClick={fetchScenario}
            className="flex items-center gap-2 px-6 py-3 bg-nunchi-800 text-white rounded-full shadow-lg hover:bg-nunchi-900 transition-all"
          >
            Next Scenario <RefreshCw size={18} />
          </button>
        </div>
      )}
    </div>
  );
};

export default ScenarioDojo;